/**
 * StoreQuotesMonitoring
 * Dashboard KPI preventivi Store HVAC scoped per ruolo:
 *  - Admin: KPI globali + breakdown per Store e per Agente + filtri admin
 *  - Agente/altro: KPI personali sui propri preventivi
 *
 * Backed by GET /api/store/quotes/stats/summary (filtra già lato server per ruolo).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, FileText, Send, Check, ShoppingCart, Euro,
  Store as StoreIcon, Users as UsersIcon, RefreshCw, AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { storeApi, formatApiError } from '../../lib/api';
import { toast } from 'sonner';

const formatCurrency = (n) =>
  `€ ${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Consegnato', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accettato', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Scaduto', color: 'bg-orange-100 text-orange-700' },
  converted: { label: 'Convertito', color: 'bg-purple-100 text-purple-700' },
};

const KpiCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${accent || 'bg-slate-100'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-0.5 truncate">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export function StoreQuotesMonitoring({ isAdmin, active }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all'); // all | 30 | 90 | 365
  const [storeFilter, setStoreFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (period !== 'all') params.period_days = Number(period);
      if (isAdmin) {
        if (storeFilter !== 'all') params.store_id = storeFilter;
        if (agentFilter !== 'all') params.agent_id = agentFilter;
      }
      const res = await storeApi.getQuotesStats(params);
      setStats(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [period, storeFilter, agentFilter, isAdmin]);

  useEffect(() => {
    if (active) loadStats();
  }, [active, loadStats]);

  if (loading && !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={`skeleton-kpi-${i}`} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-12 bg-gray-100 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">Statistiche non disponibili</p>
        <Button variant="outline" className="mt-4 gap-2" onClick={loadStats}>
          <RefreshCw className="h-4 w-4" /> Riprova
        </Button>
      </Card>
    );
  }

  const s = stats.by_status || {};
  const totalCount = stats.total_count || 0;
  const sentCount = (s.sent?.count || 0) + (s.accepted?.count || 0) + (s.converted?.count || 0);

  return (
    <div className="space-y-6">
      {/* Filtri (solo admin) */}
      {isAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Periodo</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger data-testid="monitoring-filter-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutto</SelectItem>
                    <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                    <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                    <SelectItem value="365">Ultimo anno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[200px] flex-1">
                <Label className="text-xs text-muted-foreground">Store</Label>
                <Select value={storeFilter} onValueChange={setStoreFilter}>
                  <SelectTrigger data-testid="monitoring-filter-store"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli store</SelectItem>
                    {(stats.by_store || []).map((row) => (
                      <SelectItem key={row.store_id} value={row.store_id}>
                        {row.store_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[200px] flex-1">
                <Label className="text-xs text-muted-foreground">Agente</Label>
                <Select value={agentFilter} onValueChange={setAgentFilter}>
                  <SelectTrigger data-testid="monitoring-filter-agent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli agenti</SelectItem>
                    {(stats.by_agent || []).filter((a) => a.agent_id).map((row) => (
                      <SelectItem key={row.agent_id} value={row.agent_id}>
                        {row.agent_name || row.agent_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                onClick={() => { setPeriod('all'); setStoreFilter('all'); setAgentFilter('all'); }}
                className="gap-2"
                data-testid="monitoring-filter-reset"
              >
                <RefreshCw className="h-4 w-4" /> Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={FileText} label={isAdmin ? 'Preventivi totali' : 'I miei preventivi'}
          value={totalCount}
          sub={`${stats.monthly_quotes || 0} questo mese`}
          accent="bg-blue-100 text-blue-700"
          testId="kpi-total"
        />
        <KpiCard
          icon={Send} label="Inviati"
          value={s.sent?.count || 0}
          sub={formatCurrency(s.sent?.total_value || 0)}
          accent="bg-cyan-100 text-cyan-700"
          testId="kpi-sent"
        />
        <KpiCard
          icon={Check} label="Accettati"
          value={s.accepted?.count || 0}
          sub={formatCurrency(s.accepted?.total_value || 0)}
          accent="bg-emerald-100 text-emerald-700"
          testId="kpi-accepted"
        />
        <KpiCard
          icon={ShoppingCart} label="Convertiti in vendita"
          value={stats.converted_count || 0}
          sub={formatCurrency(stats.converted_value || 0)}
          accent="bg-purple-100 text-purple-700"
          testId="kpi-converted"
        />
        <KpiCard
          icon={Euro} label="Valore totale"
          value={formatCurrency(stats.total_value)}
          sub="IVA inclusa"
          accent="bg-amber-100 text-amber-700"
          testId="kpi-total-value"
        />
        <KpiCard
          icon={TrendingUp} label="Conversion Rate"
          value={`${stats.conversion_rate || 0}%`}
          sub={`${stats.converted_count || 0} su ${sentCount} risposti`}
          accent="bg-green-100 text-green-700"
          testId="kpi-conversion-rate"
        />
        <KpiCard
          icon={FileText} label="Bozze"
          value={s.draft?.count || 0}
          sub="In preparazione"
          accent="bg-slate-100 text-slate-700"
          testId="kpi-draft"
        />
        <KpiCard
          icon={AlertCircle} label="Scaduti / Rifiutati"
          value={(s.expired?.count || 0) + (s.rejected?.count || 0)}
          sub="Da rilavorare"
          accent="bg-rose-100 text-rose-700"
          testId="kpi-lost"
        />
      </div>

      {/* Admin only: breakdown by store */}
      {isAdmin && stats.by_store && stats.by_store.length > 0 && (
        <Card data-testid="monitoring-by-store">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <StoreIcon className="h-5 w-5 text-blue-600" /> Performance per Store
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Store</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Preventivi</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Convertiti</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Conversion %</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Valore Totale</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Valore Convertito</th>
                  </tr>
                </thead>
                <tbody>
                    {stats.by_store.map((row, i) => (
                    <tr key={`${row.store_id || 'none'}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-800">{row.store_id}</td>
                      <td className="px-4 py-2.5 text-right">{row.count}</td>
                      <td className="px-4 py-2.5 text-right">{row.converted}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge className={row.conversion_rate >= 30 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                          {row.conversion_rate}%
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(row.total_value)}</td>
                      <td className="px-4 py-2.5 text-right text-purple-700 font-semibold">{formatCurrency(row.converted_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin only: breakdown by agent */}
      {isAdmin && stats.by_agent && stats.by_agent.length > 0 && (
        <Card data-testid="monitoring-by-agent">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <UsersIcon className="h-5 w-5 text-blue-600" /> Performance per Agente
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Agente</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Preventivi</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Convertiti</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Conversion %</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Valore Totale</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Valore Convertito</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_agent.map((row, i) => (
                    <tr key={row.agent_id || i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-800">{row.agent_name}</td>
                      <td className="px-4 py-2.5 text-right">{row.count}</td>
                      <td className="px-4 py-2.5 text-right">{row.converted}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge className={row.conversion_rate >= 30 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                          {row.conversion_rate}%
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(row.total_value)}</td>
                      <td className="px-4 py-2.5 text-right text-purple-700 font-semibold">{formatCurrency(row.converted_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status breakdown (visible a tutti) */}
      <Card data-testid="monitoring-by-status">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Distribuzione per stato</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {Object.entries(STATUS_META).map(([key, meta]) => {
              const row = s[key] || { count: 0, total_value: 0 };
              return (
                <div key={key} className="border border-slate-200 rounded-lg p-3" data-testid={`status-bucket-${key}`}>
                  <Badge className={meta.color}>{meta.label}</Badge>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{row.count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatCurrency(row.total_value)}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        {['admin', 'global'].includes(stats.user_scope) ? 'Vista completa: tutti gli store' : 'Vista personale: solo i tuoi preventivi'}
      </p>
    </div>
  );
}
